import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquarePlus, Bug, Lightbulb, Send, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FeedbackType = "feedback" | "feature" | "bug";

const feedbackTypes: { value: FeedbackType; label: string; icon: React.ElementType; description: string }[] = [
  { value: "feedback", label: "Feedback", icon: MessageSquarePlus, description: "General thoughts or suggestions" },
  { value: "feature", label: "Feature Request", icon: Lightbulb, description: "Something you'd love to see" },
  { value: "bug", label: "Bug Report", icon: Bug, description: "Something isn't working right" },
];

export const FeedbackDialog = ({ open, onOpenChange }: FeedbackDialogProps) => {
  const [type, setType] = useState<FeedbackType>("feedback");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !subject.trim()) return;
    setLoading(true);

    try {
      const { error } = await supabase.from("feedback").insert({
        user_id: user?.id,
        type,
        subject: subject.trim().slice(0, 200),
        message: message.trim().slice(0, 5000),
        email: user?.email || null,
      });

      if (error) throw error;

      toast({
        title: "Thanks for your feedback!",
        description: "We'll review it and get back to you if needed.",
      });

      setSubject("");
      setMessage("");
      setType("feedback");
      onOpenChange(false);
    } catch (err: any) {
      console.error("Error submitting feedback:", err);
      toast({
        title: "Couldn't submit feedback",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5 text-primary" />
            Send Feedback
          </DialogTitle>
          <DialogDescription>
            Help us improve Privy — report bugs, request features, or share your thoughts.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type selector */}
          <div className="grid grid-cols-3 gap-2">
            {feedbackTypes.map((ft) => (
              <button
                key={ft.value}
                type="button"
                onClick={() => setType(ft.value)}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-lg border p-3 text-center transition-all",
                  type === ft.value
                    ? "border-primary bg-primary/5 text-primary ring-1 ring-primary/20"
                    : "border-border hover:border-muted-foreground/30 hover:bg-muted/50 text-muted-foreground"
                )}
              >
                <ft.icon className="h-5 w-5" />
                <span className="text-xs font-medium">{ft.label}</span>
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-subject">Subject</Label>
            <Input
              id="feedback-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                type === "bug"
                  ? "e.g. Chat page crashes when…"
                  : type === "feature"
                  ? "e.g. Add dark mode toggle"
                  : "e.g. Love the new dashboard!"
              }
              required
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-message">Details</Label>
            <Textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                type === "bug"
                  ? "What happened? What did you expect? Steps to reproduce…"
                  : type === "feature"
                  ? "Describe the feature and how it would help you…"
                  : "Tell us what's on your mind…"
              }
              required
              rows={4}
              maxLength={5000}
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/5000
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !message.trim() || !subject.trim()}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
